import EmptyState from "@/components/EmptyState";
import Table, {
  TableFooter,
  TableControls,
  buildCopyTextColumn,
  buildActionColumn,
  buildExpandableCountColumn,
  buildTagColumn
} from "@/components/Table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Path } from "@/router";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import SearchInput from "@/components/SearchInput";
import FacilitySideSheet from "@/features/facility/components/FacilitySideSheet";
import NotesTable from "@/features/global/components/NotesTable";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import ErrorBoundary from "@/components/ErrorBoundary";
import useCapabilities, {
  useCanManageCommitteesBool,
  useCanManageFacilities,
  useCanViewFacilities,
  useHasCapabilityBool,
  DOMAIN,
  PERMISSION
} from "@/hooks/useCapabilities";
import { NoteNotableValues } from "@/types";
import { EditGovernanceRoleModal } from "@/pages/facilities/[facilityId]/people/governance/_EditGovernanceRoleModal";
import { ArchiveGovernanceRoleDialog } from "@/pages/facilities/[facilityId]/people/governance/_ArchiveGovernanceRoleDialog";
import { GovernanceRoleFilter } from "@/pages/facilities/[facilityId]/people/governance/_GovernanceRoleFilter";
import { useGovernanceRoleArchival } from "@/pages/facilities/[facilityId]/people/governance/useGovernanceRoleArchival";
import { useGovernanceRoleFilter } from "@/pages/facilities/[facilityId]/people/governance/useGovernanceRoleFilter";
import { useFlags } from "@/hooks/useFeatureFlags";
import { canManageSectionCommittee } from "@/pages/sections/[sectionId]/operations/canManageSectionCommittee";
import { useCanManageAnySectionCommittees } from "@/pages/sections/[sectionId]/operations/useCanManageSectionCommittees";
import { EditSectionGovernanceRoleModal } from "@/pages/sections/[sectionId]/operations/_components/EditSectionGovernanceRoleModal";
import { getSectionGovernanceTargetType } from "@/pages/sections/[sectionId]/operations/_components/getSectionGovernanceTargetType";
import { useSectionGovernanceRoleArchival } from "@/pages/sections/[sectionId]/operations/_components/useSectionGovernanceRoleArchival";
import { PersonAddGovernanceRoleModal } from "./_PersonAddGovernanceRoleModal";
import { GovernanceOrgTypeFilter } from "./_GovernanceOrgTypeFilter";
import { PERSON_GOVERNANCE_ROLES_QUERY } from "./queries";
import { useGovernanceOrgTypeFilter } from "./useGovernanceOrgTypeFilter";
import {
  getGovernanceStatusLabel,
  getGovernanceStatusTagType,
  getGovernanceStatusTagStyle,
  matchesPersonGovernanceSearch,
  matchesGovernanceRoleFilter,
  matchesPersonGovernanceOrgTypeFilter,
  buildGovernanceRoleFilterOptions,
  buildPersonGovernanceOrgTypeFilterOptions,
  getEffectiveGovernanceSearch,
  clearGovernanceFilters,
  mergePersonGovernanceRoleSources,
  PERSON_GOVERNANCE_ORG_TYPE_LABELS,
  getPersonGovernanceSectionCatalog,
  isFacilityPersonGovernanceRole
} from "./_utils";
import type { PersonGovernanceRole } from "./types";
import { GovernanceStatus } from "./types";

export function PersonGovernanceTable({ pgaId }: { pgaId: string }) {
  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });
  const flags = useFlags();
  const sectionGovernanceEnabled = flags["backoffice-section-governance"] === true;
  const { params, addSearchParam, deleteSearchParams } = useParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const { selectedRoleIds, onSelect: onSelectRole, onClear: onClearRole } = useGovernanceRoleFilter();
  const { selectedOrgTypes, onSelect: onSelectOrgType, onClear: onClearOrgType } = useGovernanceOrgTypeFilter();
  const urlShowArchived = params.showArchived === "true";
  const [showArchived, setShowArchivedState] = useState(urlShowArchived);

  const setShowArchived = (value: boolean) => {
    setShowArchivedState(value);
    if (value) {
      addSearchParam("showArchived", "true");
    } else {
      deleteSearchParams("showArchived");
    }
  };

  const { data, loading, error } = useQuery(PERSON_GOVERNANCE_ROLES_QUERY, {
    variables: { pgaId },
    skip: !canViewPersonCareer || !pgaId,
    errorPolicy: "all"
  });

  const {
    archiveRole: archiveFacilityRole,
    unarchiveRole: unarchiveFacilityRole,
    archiving: archivingFacility
  } = useGovernanceRoleArchival(PERSON_GOVERNANCE_ROLES_QUERY);
  const {
    archiveRole: archiveSectionRole,
    unarchiveRole: unarchiveSectionRole,
    archiving: archivingSection
  } = useSectionGovernanceRoleArchival(PERSON_GOVERNANCE_ROLES_QUERY);

  const navigate = useNavigate();
  const location = useLocation();
  const { id: currentUserId } = useCapabilities();
  const canManageFacilities = useCanManageFacilities();
  const canViewFacilities = useCanViewFacilities();
  const canViewSection = useHasCapabilityBool({ domain: DOMAIN.SECTIONS, permission: PERMISSION.viewSection });
  const hasManageCommitteesCapability = useCanManageCommitteesBool();
  const { canManageAnySection, isGlobalAdmin, assignedSectionLegacyIds } = useCanManageAnySectionCommittees();
  const canAddFacility = canManageFacilities;
  const canAddSection = sectionGovernanceEnabled && canManageAnySection;
  const canAddGovernance = canAddFacility || canAddSection;
  const { Menu, setMenu } = useTableRowMenu();
  const [isFacilitySideSheetOpen, setIsFacilitySideSheetOpen] = useState(false);
  const [selectedFacilityId, setSelectedFacilityId] = useState<string | undefined>(undefined);
  const [roleToEdit, setRoleToEdit] = useState<PersonGovernanceRole | null>(null);
  const [roleToArchive, setRoleToArchive] = useState<PersonGovernanceRole | null>(null);
  const [roleToAddNote, setRoleToAddNote] = useState<PersonGovernanceRole | null>(null);
  const [isAddGovernanceRoleModalOpen, setIsAddGovernanceRoleModalOpen] = useState(false);

  const allRoles = useMemo<PersonGovernanceRole[]>(
    () =>
      mergePersonGovernanceRoleSources({
        facilityGovernanceRoles: data?.person?.facilityGovernanceRoles,
        governanceRoles: data?.person?.governanceRoles
      }).filter((role) => sectionGovernanceEnabled || isFacilityPersonGovernanceRole(role)),
    [data, sectionGovernanceEnabled]
  );

  const wasAllArchived = useRef<boolean | null>(null);
  useEffect(() => {
    if (loading || !data) {
      return;
    }

    const allArchived = allRoles.length > 0 && allRoles.every((role) => role.status === GovernanceStatus.ARCHIVED);
    const previouslyAllArchived = wasAllArchived.current;
    wasAllArchived.current = allArchived;

    if (allArchived && previouslyAllArchived !== true) {
      setShowArchivedState(true);
    }
  }, [loading, data, allRoles]);

  const effectiveSearch = getEffectiveGovernanceSearch(searchValue, debouncedSearch, params.search);

  const roles = useMemo(
    () =>
      allRoles
        .filter((role) => showArchived || role.status === GovernanceStatus.ACTIVE)
        .filter((role) => matchesPersonGovernanceSearch(role, effectiveSearch))
        .filter((role) => matchesGovernanceRoleFilter(role, selectedRoleIds))
        .filter((role) => matchesPersonGovernanceOrgTypeFilter(role, selectedOrgTypes)),
    [allRoles, showArchived, effectiveSearch, selectedRoleIds, selectedOrgTypes]
  );

  const roleFilterOptions = useMemo(() => buildGovernanceRoleFilterOptions(allRoles), [allRoles]);
  const orgTypeFilterOptions = useMemo(() => buildPersonGovernanceOrgTypeFilterOptions(allRoles), [allRoles]);

  const personId = data?.person?.id ?? "";
  const fallbackSections = useMemo(() => getPersonGovernanceSectionCatalog(allRoles), [allRoles]);

  const filterState = {
    orgType: selectedOrgTypes.length > 0,
    role: selectedRoleIds.length > 0
  };

  const activeFilterCount = [
    showArchived,
    effectiveSearch,
    selectedRoleIds.length > 0,
    selectedOrgTypes.length > 0
  ].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0 || roles.length < allRoles.length;

  function canManageRole(role: PersonGovernanceRole): boolean {
    if (isFacilityPersonGovernanceRole(role)) {
      return canManageFacilities;
    }

    return canManageSectionCommittee({
      isGlobalAdmin,
      assignedSectionLegacyIds,
      sectionLegacyId: role.section?.legacyId,
      hasManageCommitteesCapability
    });
  }

  function handleClearAllFilters() {
    clearGovernanceFilters({
      setShowArchived: setShowArchivedState,
      setSearchValue,
      deleteSearchParams
    });
  }

  async function handleArchiveGovernanceRole() {
    if (!roleToArchive) {
      return;
    }

    const success = isFacilityPersonGovernanceRole(roleToArchive)
      ? await archiveFacilityRole(roleToArchive.id)
      : await archiveSectionRole(roleToArchive.id);
    if (success) {
      setRoleToArchive(null);
    }
  }

  async function handleUnarchiveGovernanceRole(role: PersonGovernanceRole) {
    if (isFacilityPersonGovernanceRole(role)) {
      await unarchiveFacilityRole(role.id);
      return;
    }

    await unarchiveSectionRole(role.id);
  }

  function buildOrganizationPath(role: PersonGovernanceRole): Path | null {
    const returnTo = location.pathname;
    if (role.facility) {
      if (!canViewFacilities) {
        return null;
      }
      return `/facilities/${role.facility.id}/people/governance?return_to=${encodeURIComponent(returnTo)}` as Path;
    }
    if (role.section) {
      if (!canViewSection) {
        return null;
      }
      return `/sections/${role.section.id}/operations/governance?return_to=${encodeURIComponent(returnTo)}` as Path;
    }
    return null;
  }

  if (error && !data?.person) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Governance"
        message="Please try again or contact support if the problem persists."
        className="mt-12"
      />
    );
  }

  const columns = [
    buildCopyTextColumn<PersonGovernanceRole>({
      accessorKey: "organizationName",
      header: "Section or Facility Name",
      copyFieldLabel: "Section or Facility Name",
      size: 280,
      enableSorting: false,
      getText: (row) => row.organizationName,
      getSecondaryText: (row) => row.organizationSecondaryText,
      shouldShowCopyButton: (row) => !!row.organizationName,
      meta: { headerClassName: "hide-header-in-column" }
    }),
    {
      accessorFn: (row: PersonGovernanceRole) => PERSON_GOVERNANCE_ORG_TYPE_LABELS[row.orgType],
      header: "Type",
      id: "orgType",
      size: 120,
      enableSorting: false
    },
    {
      accessorFn: (row: PersonGovernanceRole) => row.title.title,
      header: "Role",
      id: "role",
      enableSorting: false
    },
    buildExpandableCountColumn<PersonGovernanceRole>({
      accessorKey: "notes",
      header: "Notes",
      getCount: (row) => row.notes.length,
      size: 100,
      minSize: 80,
      showExpandIcon: true
    }),
    buildTagColumn<PersonGovernanceRole>({
      accessorKey: "status",
      header: "Status",
      getTagLabel: (row) => getGovernanceStatusLabel(row.status),
      getTagType: (row) => getGovernanceStatusTagType(row.status),
      getTagStyle: (row) => getGovernanceStatusTagStyle(row.status),
      meta: {
        headerClassName: "hide-header-in-column"
      }
    }),
    buildActionColumn<PersonGovernanceRole>({
      getActions: (row) => {
        const actions: Action[] = [];
        const canManage = canManageRole(row);

        if (canManage && row.status === GovernanceStatus.ACTIVE) {
          actions.push({
            text: "Edit Role",
            section: "Manage",
            onClick: () => setRoleToEdit(row)
          });
        }

        if (canManage && row.status === GovernanceStatus.ARCHIVED) {
          actions.push({
            text: "Unarchive Role",
            section: "Manage",
            iconName: "unarchive",
            onClick: () => handleUnarchiveGovernanceRole(row)
          });
        }

        if (row.facility && canViewFacilities) {
          actions.push({
            text: "View Facility",
            section: "Actions",
            onClick: () => {
              navigate(`/facilities/${row.facility?.id}`);
            }
          });
        }

        if (row.section && canViewSection) {
          actions.push({
            text: "View Section",
            section: "Actions",
            onClick: () => {
              const path = buildOrganizationPath(row);
              if (path) {
                navigate(path);
              }
            }
          });
        }

        if (canManage) {
          actions.push({
            text: "Add Note",
            section: "Actions",
            onClick: () => setRoleToAddNote(row)
          });
        }

        if (canManage && row.status === GovernanceStatus.ACTIVE) {
          actions.push({
            text: "Archive Role",
            iconName: "archive",
            destructive: true,
            onClick: () => setRoleToArchive(row)
          });
        }

        return actions;
      },
      onViewClick: (row) => {
        if (row.facility && canViewFacilities) {
          setSelectedFacilityId(row.facility.id);
          setIsFacilitySideSheetOpen(true);
          return;
        }
        const path = buildOrganizationPath(row);
        if (path) {
          navigate(path);
        }
      },
      showViewButton: (row) => {
        if (row.facility) {
          return canViewFacilities;
        }
        return Boolean(buildOrganizationPath(row));
      },
      viewButtonTitle: "View details",
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];

  return (
    <>
      <Menu />
      <div className="pt-12" />
      <Table
        data={roles}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        getRowCanExpand={(row) => row.original.notes.length > 0}
        getRowLink={(row) => {
          const path = buildOrganizationPath(row.original);
          return path ? { to: path } : null;
        }}
        columns={columns}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            actions={
              canAddGovernance
                ? [
                    {
                      text: "Add Governance Role",
                      iconName: "add",
                      variant: "filled",
                      onClick: () => setIsAddGovernanceRoleModalOpen(true),
                      testId: "add-governance-role-button"
                    }
                  ]
                : []
            }
            filterChips={[
              {
                label: "Show Archived",
                onClick: () => setShowArchived(!showArchived),
                selected: showArchived
              }
            ]}
            useExpandedFilters
            expandedFiltersState={filterState}
            activeFilters={activeFilterCount}
            onClearAllFilters={handleClearAllFilters}
            onSortClick={onSortClick}
          >
            <SearchInput
              data-name="search"
              placeholder="Search Governance Roles"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
            {orgTypeFilterOptions.length >= 2 ? (
              <GovernanceOrgTypeFilter
                name="orgType"
                options={orgTypeFilterOptions}
                selectedValues={selectedOrgTypes}
                onSelect={onSelectOrgType}
                onClear={onClearOrgType}
              />
            ) : null}
            <GovernanceRoleFilter
              name="role"
              options={roleFilterOptions}
              selectedValues={selectedRoleIds}
              onSelect={onSelectRole}
              onClear={onClearRole}
            />
          </TableControls>
        )}
        renderExpandedRow={({ row }) => (
          <NotesTable
            variant="details-panel"
            data={row.original.notes}
            title={`Notes (${row.original.notes.length})`}
          />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Governance Roles"
            caption="There are no governance roles associated with this person."
            iconName="person"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="person" />
        )}
        renderFooter={() => <TableFooter totalRowCount={roles.length} />}
      />
      {canViewFacilities ? (
        <FacilitySideSheet
          open={isFacilitySideSheetOpen}
          onClose={() => {
            setIsFacilitySideSheetOpen(false);
            setSelectedFacilityId(undefined);
          }}
          facilityId={selectedFacilityId ?? null}
        />
      ) : null}
      {roleToEdit && isFacilityPersonGovernanceRole(roleToEdit) ? (
        <EditGovernanceRoleModal
          open
          role={roleToEdit}
          facility={roleToEdit.facility}
          onClose={() => setRoleToEdit(null)}
          refetchQueries={[PERSON_GOVERNANCE_ROLES_QUERY]}
        />
      ) : null}
      {roleToEdit && !isFacilityPersonGovernanceRole(roleToEdit) ? (
        <EditSectionGovernanceRoleModal
          open
          role={roleToEdit}
          organizationName={roleToEdit.organizationName}
          organizationSecondaryText={roleToEdit.organizationSecondaryText}
          targetType={getSectionGovernanceTargetType(roleToEdit.section)}
          onClose={() => setRoleToEdit(null)}
          refetchQueries={[PERSON_GOVERNANCE_ROLES_QUERY]}
        />
      ) : null}
      {isAddGovernanceRoleModalOpen && (
        <PersonAddGovernanceRoleModal
          open
          personId={personId}
          canAddFacility={canAddFacility}
          canAddSection={canAddSection}
          fallbackSections={fallbackSections}
          onClose={() => setIsAddGovernanceRoleModalOpen(false)}
          refetchQueries={[PERSON_GOVERNANCE_ROLES_QUERY]}
        />
      )}
      <ArchiveGovernanceRoleDialog
        open={roleToArchive !== null}
        onClose={() => setRoleToArchive(null)}
        onConfirm={handleArchiveGovernanceRole}
        isLoading={archivingFacility || archivingSection}
        facilityName={roleToArchive?.organizationName}
        roleLabel={roleToArchive?.title.title}
      />
      {roleToAddNote && (
        <AddEditNoteSideSheet
          open
          onClose={() => setRoleToAddNote(null)}
          notableId={roleToAddNote.id}
          notableType={NoteNotableValues.FACILITY_ASSOCIATION}
          refetchQueries={[PERSON_GOVERNANCE_ROLES_QUERY]}
          currentUserId={currentUserId || undefined}
          pgaId={pgaId}
          objectType="person"
          onSaveSuccess={() => setRoleToAddNote(null)}
        />
      )}
    </>
  );
}
